import { Injectable, inject } from '@angular/core';
import { DeviceService } from './device.service';

export interface PrintNodeConfig {
  configImpresoraId: number;
  aliasImpresora: string;
  printerName: string;
  widthMm: number;
  sucursalId: number;
}

@Injectable({
  providedIn: 'root',
})
export class PrintNodeService {
  private readonly deviceService = inject(DeviceService);
  private storageKeyPrefix = 'print_node_config';

  /**
   * Genera la clave de almacenamiento para una sucursal específica
   */
  private getStorageKey(sucursalId: number): string {
    return `${this.storageKeyPrefix}_${sucursalId}`;
  }

  /**
   * Verifica si existe configuración para una sucursal específica
   */
  isConfigured(sucursalId: number): boolean {
    if (!this.deviceService.isBrowser()) return false;
    return !!localStorage.getItem(this.getStorageKey(sucursalId));
  }

  /**
   * Guarda la configuración para una sucursal específica
   */
  saveConfig(config: PrintNodeConfig) {
    if (!this.deviceService.isBrowser()) return;
    const key = this.getStorageKey(config.sucursalId);
    localStorage.setItem(key, JSON.stringify(config));
  }

  /**
   * Obtiene la configuración de una sucursal específica
   */
  getConfigBySucursal(sucursalId: number): PrintNodeConfig | null {
    if (!this.deviceService.isBrowser()) return null;
    const raw = localStorage.getItem(this.getStorageKey(sucursalId));
    return raw ? JSON.parse(raw) : null;
  }

  /**
   * Obtiene la configuración (por compatibilidad con código existente)
   * Busca la config de una sucursal específica
   */
  getConfig(sucursalId?: number): PrintNodeConfig | null {
    if (!this.deviceService.isBrowser()) return null;
    if (!sucursalId) return null;
    return this.getConfigBySucursal(sucursalId);
  }

  /**
   * Limpia la configuración de una sucursal específica
   */
  clearBySucursal(sucursalId: number) {
    if (!this.deviceService.isBrowser()) return;
    localStorage.removeItem(this.getStorageKey(sucursalId));
  }

  /**
   * Limpia todas las configuraciones (por compatibilidad)
   */
  clear() {
    if (!this.deviceService.isBrowser()) return;
    // Limpia todas las claves que empiezan con el prefijo
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith(this.storageKeyPrefix)) {
        localStorage.removeItem(key);
      }
    });
  }
}
